// Flashcards for one verb table: the front shows the subject and the tense, the back shows the form.
// Enter / Space flips the card, the arrow keys move between cards. Self-practice only: nothing is graded here.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { Tense } from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import { useMemo, useState } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    RotateCcw,
    ScanSearch,
    Shuffle
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { hashString, seededShuffle } from '@/prakrit/lib/grammar/questions'
import {
    NUMBER_LABEL,
    PERSON_LABEL,
    SUBJECT,
    SUBJECT_GLOSS,
    TENSE_LABEL
} from '@/prakrit/lib/grammar/labels'
import { Bi } from '@/prakrit/components/common/bi'
import { AudioButton } from '@/prakrit/components/common/audio-button'
import { useWordPanel } from '@/prakrit/components/common/word-panel'
import { StatusBadge } from '@/prakrit/components/common/status-badge'
import { SECONDARY_BUTTON, hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { CELL_ORDER } from '@/prakrit/components/grammar/labs/verb-data'

interface VerbFlashcardsProps {
    root: LabRoot
    tense: Tense
    showAlt: boolean
}

export const VerbFlashcards: FC<VerbFlashcardsProps> = ({ root, tense, showAlt }): ReactNode => {
    const { open } = useWordPanel()
    const cards = useMemo(() => CELL_ORDER.map(([person, number]) => {
        const cell = root.tables[tense]?.[person]?.[number] ?? []

        return {
            person,
            number,
            core: cell.filter(f => f.status === 'core').map(f => f.form),
            alt: cell.filter(f => f.status === 'alt').map(f => f.form)
        }
    }), [root, tense])

    const [order, setOrder] = useState<number[]>(() => cards.map((_, i) => i))
    const [position, setPosition] = useState(0)
    const [flipped, setFlipped] = useState(false)
    const [rounds, setRounds] = useState(0)
    const [seen, setSeen] = useState<number[]>([])

    const index = order[position] ?? 0
    const card = cards[index]
    const total = cards.length

    if (!card || card.core.length === 0) {
        return (
            <p className='rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground'>
                <Bi hi='इस काल की तालिका इस धातु के लिए नहीं है।' en='This root has no table for this tense.' />
            </p>
        )
    }

    const subject = SUBJECT[card.person][card.number]
    const gloss = SUBJECT_GLOSS[card.person][card.number]
    const mainForm = card.core[0]

    const go = (next: number) => {
        setFlipped(false)
        setPosition((next + total) % total)
    }

    const flip = () => {
        setFlipped(f => !f)

        if (!seen.includes(index)) setSeen(list => [...list, index])
    }

    const shuffle = () => {
        const nextRound = rounds + 1

        setRounds(nextRound)
        setOrder(seededShuffle(cards.map((_, i) => i), hashString(`${root.root}|${tense}|${nextRound}`)))
        setPosition(0)
        setFlipped(false)
        setSeen([])
    }

    const restart = () => {
        setOrder(cards.map((_, i) => i))
        setPosition(0)
        setFlipped(false)
        setSeen([])
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            go(position + 1)
        }
        else if (event.key === 'ArrowLeft') {
            event.preventDefault()
            go(position - 1)
        }
    }

    const face = 'absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-2xl border px-5 py-6 text-center [backface-visibility:hidden]'

    return (
        <div className='flex flex-col gap-5'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
                <p className='text-sm text-muted-foreground'>
                    <Bi
                        hi='कार्ड पर टैप करें (या Enter दबाएँ) तो रूप दिखेगा; तीर-कुंजियों से अगला कार्ड।'
                        en='Tap the card (or press Enter) to see the form; the arrow keys move to the next card.'
                    />
                </p>
                <p className='text-sm font-medium'>
                    <span lang='hi' className='hindi'>कार्ड {hiDigits(position + 1)} / {hiDigits(total)}</span>
                    <span aria-hidden='true' className='text-muted-foreground'> · </span>
                    <span lang='en' className='text-muted-foreground'>{seen.length} of {total} turned</span>
                </p>
            </div>

            <div role='group' aria-label='स्मृति-कार्ड · Flashcards' onKeyDown={onKeyDown} className='flex flex-col gap-4'>
                <button
                    type='button'
                    onClick={flip}
                    aria-pressed={flipped}
                    aria-label={flipped
                        ? `${subject} ${card.core.join(', ')} — पलटें · flip back`
                        : `${subject}: ${PERSON_LABEL[card.person].hi} ${NUMBER_LABEL[card.number].hi}, ${TENSE_LABEL[tense].hi}. रूप देखने के लिए पलटें · flip to see the form`}
                    className='group relative block w-full rounded-2xl text-left [perspective:1400px] focus-visible:outline-offset-4'
                >
                    <span
                        className={cn(
                            'relative block min-h-[17rem] transition-transform duration-500 ease-out [transform-style:preserve-3d] sm:min-h-[16rem]',
                            flipped && '[transform:rotateY(180deg)]'
                        )}
                    >
                        <span aria-hidden={flipped} className={cn(face, 'border-border bg-card shadow-card')}>
                            <span className='text-xs font-semibold uppercase tracking-[0.08em] text-saffron-strong'>
                                <span lang='hi' className='hindi normal-case tracking-normal'>{TENSE_LABEL[tense].hi}</span>
                                <span aria-hidden='true'> · </span>
                                <span lang='en'>{TENSE_LABEL[tense].en}</span>
                            </span>
                            <span lang='pra' className='pra pra-display'>{subject}</span>
                            <Bi hi={gloss.hi} en={gloss.en} layout='inline' className='justify-center text-base' />
                            <span className='flex flex-wrap justify-center gap-x-2 text-sm text-muted-foreground'>
                                <span lang='hi' className='hindi'>{PERSON_LABEL[card.person].hi} {NUMBER_LABEL[card.number].hi}</span>
                                <span lang='en' data-ex='en'>({NUMBER_LABEL[card.number].en})</span>
                            </span>
                            <span className='mt-2 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground'>
                                <RotateCcw className='size-3.5' aria-hidden='true' />
                                <span lang='hi' className='hindi'>पलटें</span>
                                <span lang='en'>Flip</span>
                            </span>
                        </span>

                        <span aria-hidden={!flipped} className={cn(face, 'border-prakrit/30 bg-surface-muted shadow-card [transform:rotateY(180deg)]')}>
                            <span lang='pra' className='pra pra-lg text-muted-foreground'>{subject}</span>
                            <span lang='pra' className='pra pra-display'>{card.core.join(' / ')}</span>
                            {showAlt && card.alt.length > 0 && (
                                <span className='flex flex-wrap items-center justify-center gap-1.5'>
                                    <span lang='pra' className='font-prakrit text-lg text-terracotta'>{card.alt.join(', ')}</span>
                                    <StatusBadge kind='alt' />
                                </span>
                            )}
                            <span className='text-sm text-muted-foreground'>
                                <span lang='hi' className='hindi'>{PERSON_LABEL[card.person].hi} {NUMBER_LABEL[card.number].hi} · {TENSE_LABEL[tense].hi}</span>
                            </span>
                        </span>
                    </span>
                </button>

                <span aria-live='polite' className='sr-only'>
                    {flipped ? `${subject} ${card.core.join(', ')}` : ''}
                </span>

                <div className='flex flex-wrap items-center justify-between gap-3'>
                    <div className='flex flex-wrap gap-2'>
                        <button type='button' onClick={() => go(position - 1)} className={SECONDARY_BUTTON}>
                            <ChevronLeft className='size-4' aria-hidden='true' />
                            <Bi hi='पिछला' en='Previous' layout='inline' enClassName='text-xs text-muted-foreground' always />
                        </button>
                        <button type='button' onClick={() => go(position + 1)} className={SECONDARY_BUTTON}>
                            <Bi hi='अगला' en='Next' layout='inline' enClassName='text-xs text-muted-foreground' always />
                            <ChevronRight className='size-4' aria-hidden='true' />
                        </button>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                        <button type='button' onClick={shuffle} className={SECONDARY_BUTTON}>
                            <Shuffle className='size-4' aria-hidden='true' />
                            <Bi hi='मिलाएँ' en='Shuffle' layout='inline' enClassName='text-xs text-muted-foreground' always />
                        </button>
                        <button type='button' onClick={restart} className={SECONDARY_BUTTON}>
                            <RotateCcw className='size-4' aria-hidden='true' />
                            <Bi hi='शुरू से' en='Start again' layout='inline' enClassName='text-xs text-muted-foreground' always />
                        </button>
                    </div>
                </div>
            </div>

            <div aria-hidden='true' className='flex justify-center gap-1.5'>
                {order.map((cardIndex, i) => (
                    <span
                        key={cardIndex}
                        className={cn(
                            'h-1.5 rounded-full transition-all',
                            i === position ? 'w-6 bg-saffron' : seen.includes(cardIndex) ? 'w-3 bg-prakrit/45' : 'w-3 bg-border'
                        )}
                    />
                ))}
            </div>

            {flipped && (
                <div className='flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-4 py-3'>
                    <button type='button' onClick={() => open(mainForm)} className={SECONDARY_BUTTON}>
                        <ScanSearch className='size-4' aria-hidden='true' />
                        <span lang='hi' className='hindi'>शब्द-विवरण</span>
                        <span lang='en' className='text-xs text-muted-foreground'>Word Explorer</span>
                    </button>
                    <AudioButton text={mainForm} showMissing />
                </div>
            )}
        </div>
    )
}
